import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Card,
  CardContent,
  Stack,
  useTheme
} from '@mui/material';
import { motion } from 'framer-motion';
import SEO from '../utils/seo/SEO';
import EmailIcon from '@mui/icons-material/Email';
import CallIcon from '@mui/icons-material/Call';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import Grid from '../utils/GridWrapper';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const Contact: React.FC = () => {
  const theme = useTheme();
  
  // Contact info
  const contactInfo = [
    {
      icon: <EmailIcon fontSize="large" />,
      title: 'Email',
      content: 'todfather@gmail.com',
      link: 'mailto:todfather@gmail.com'
    },
    {
      icon: <CallIcon fontSize="large" />,
      title: 'Phone',
      content: 'Available upon request',
      link: ''
    },
    {
      icon: <LinkedInIcon fontSize="large" />,
      title: 'LinkedIn',
      content: 'linkedin.com/in/todewasko',
      link: 'https://www.linkedin.com/in/todewasko/'
    },
    {
      icon: <LocationOnIcon fontSize="large" />,
      title: 'Office',
      content: 'Orlando, Florida, USA',
      link: 'https://www.google.com/maps/place/Lake+Nona+Region,+Orlando,+FL/@28.3931474,-81.3174365,13z/data=!3m1!4b1!4m15!1m8!3m7!1s0x88e773d8fecdbc77:0xac3b2063ca5bf9e!2sOrlando,+FL!3b1!8m2!3d28.5383832!4d-81.3789269!16zL20vMHBseTA!3m5!1s0x88dd89df98dfba79:0x5ca03d33f55f241e!8m2!3d28.3765852!4d-81.2585677!16s%2Fm%2F09gg841?entry=ttu&g_ep=EgoyMDI1MDQxNi4xIKXMDSoJLDEwMjExNDUzSAFQAw%3D%3D'
    }
  ];

  return (
    <>
      <SEO 
        title="Contact Ewasko Consulting | Get in Touch for Product Strategy Consulting"
        description="Contact Tod Ewasko for expert product management, strategy consulting, and leadership services. Based in Orlando, FL with services available nationwide."
        keywords="product management consultant contact, Tod Ewasko, product strategy consulting, contact us"
        canonicalUrl="https://todfather.com/contact"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "url": "https://todfather.com/contact",
          "headline": "Contact Ewasko Consulting",
          "description": "Get in touch with Tod Ewasko for expert product management consulting services.",
          "mainEntity": {
            "@type": "LocalBusiness",
            "name": "Ewasko Consulting",
            "email": "todfather@gmail.com",
            "location": {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Orlando",
                "addressRegion": "FL",
                "addressCountry": "US"
              }
            },
            "sameAs": ["https://www.linkedin.com/in/todewasko/"]
          }
        }}
      />
      <Box>
        {/* Hero Section */}
        <Box 
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            py: { xs: 8, md: 12 },
            position: 'relative'
          }}
        >
          <Container maxWidth="lg">
            <Grid container justifyContent="center">
              <Grid item xs={12} md={8} textAlign="center">
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={fadeIn}
                >
                  <Typography 
                    variant="overline" 
                    component="div" 
                    sx={{ opacity: 0.8, fontWeight: 'bold', letterSpacing: 2 }}
                  >
                    Contact Us
                  </Typography>
                  <Typography 
                    variant="h2" 
                    component="h1"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Let's Start a Conversation
                  </Typography>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      mb: 4, 
                      maxWidth: '800px',
                      opacity: 0.9,
                      mx: 'auto'
                    }}
                  >
                    Ready to transform your product strategy? We're here to help. Reach out directly using the contact information below.
                  </Typography>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Contact Information Section */}
        <Box sx={{ py: { xs: 8, md: 10 } }}>
          <Container maxWidth="lg">
            <Grid container justifyContent="center">
              {/* Contact Information - Centered and wider */}
              <Grid item xs={12} md={8}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeIn}
                >
                  <Box sx={{ mb: 4, textAlign: 'center' }}>
                    <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Contact Information
                    </Typography>
                    <Typography variant="body1" paragraph color="textSecondary">
                      You can reach us directly using the following contact details.
                    </Typography>
                  </Box>
                  
                  <Stack spacing={3}>
                    {contactInfo.map((info, index) => (
                      <Card 
                        key={index}
                        elevation={2}
                        sx={{ 
                          transition: 'all 0.3s ease',
                          '&:hover': { 
                            transform: 'translateY(-5px)',
                            boxShadow: theme.shadows[8]
                          }
                        }}
                      >
                        <CardContent sx={{ 
                          display: 'flex',
                          alignItems: 'center',
                          p: 3
                        }}>
                          <Box 
                            sx={{ 
                              color: 'primary.main', 
                              mr: 2,
                              display: 'flex'
                            }}
                          >
                            {info.icon}
                          </Box>
                          <Box>
                            <Typography variant="h6" component="h3" gutterBottom>
                              {info.title}
                            </Typography>
                            <Typography 
                              variant="body1" 
                              component="a" 
                              href={info.link}
                              sx={{ 
                                color: 'text.primary',
                                textDecoration: 'none',
                                '&:hover': {
                                  color: 'primary.main'
                                }
                              }}
                            >
                              {info.content}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                  
                  {/* Office Hours */}
                  <Box sx={{ mt: 4, p: 3, bgcolor: 'background.default', borderRadius: 2 }}>
                    <Typography variant="h6" component="h3" gutterBottom>
                      Office Hours
                    </Typography>
                    <Typography variant="body2" component="p">
                      Monday - Friday: 9:00 AM - 5:00 PM PST
                    </Typography>
                    <Typography variant="body2" component="p">
                      Saturday - Sunday: Closed
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default Contact;